import { computed, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import useAppShell from '../composables/useAppShell';
import useSharedLoads, { trackSharedLoad } from '../composables/useSharedLoads';

// Everything the classic layout does on every page (useAppShell, shared with DashboardLayout),
// plus what the classic NavBar loads: site settings, the bell's notifications and the team's
// running servers over CsServerChannel. Same endpoints and store state as NavBar.vue.
export default function useV5Shell() {
  const store = useStore();
  const auth = useAuth();
  const shell = useAppShell();
  const user = computed(() => auth.user() || {});

  // Site settings: NavBar writes them straight into the store, pages read store.state.siteSettings
  const siteSettings = computed(() => store.state.siteSettings || []);
  trackSharedLoad('settings', () => axios.get('settings')
    .then(({ data }) => { store.state.siteSettings = data.settings; }));
  const setting = (name) => {
    const found = siteSettings.value.find((s) => s.name === name)?.enabled;
    return found === undefined ? true : found;
  };
  const serverLauncherDisabled = computed(() => (
    !setting('start_cs2_server') && !setting('start_csgo_server')
  ));

  // Same rule as NavBar.canStartServer: a paid user whose active team may start servers
  const canStartServer = computed(() => {
    if (!user.value.teams || serverLauncherDisabled.value) return false;
    const team = user.value.teams.find((x) => x.id === store.state.activeTeam?.id);
    return user.value.access_level > 0 && !!team?.can_start_server;
  });

  // The bell (level 0); level 1 is the popup useAppShell loads
  const notices = ref([]);
  axios.get('/notifications?level=0').then(({ data }) => { notices.value = data; }).catch(() => {});
  const unseenNotices = computed(() => notices.value.filter((n) => !n.seen));
  const seeNotice = (n) => axios.post(`/notifications/${n.id}/see`)
    .then(({ data }) => { notices.value = data; }).catch(() => {});
  const clearNotices = () => axios.post('/notifications/see_all')
    .then(({ data }) => { notices.value = data; }).catch(() => {});

  // Running servers: activeServer is the one you started, activeServers the team's online ones
  const activeServer = computed(() => store.state.activeServer);
  const activeServers = computed(() => store.state.activeServers || []);
  let serverSubscription = null;
  let gone = false;
  function applyServers(servers, onlyMineOnline) {
    store.state.activeServers = servers.filter((s) => s.status === 'online');
    store.commit('setActiveServer', servers.find((s) => s.started_by?.id === user.value.id
      && (!onlyMineOnline || s.status !== 'terminated')) || null);
  }
  function subscribeToServers(teamId) {
    // A page left before /cs_servers/running answered must not open a socket nobody closes
    if (serverSubscription || gone) return;
    serverSubscription = shell.getCableConsumer().subscriptions.create(
      { channel: 'CsServerChannel', team_id: teamId },
      {
        received: (data) => {
          applyServers(data.servers || [], true);
          useSharedLoads().status.servers = 'ready'; // a push is as good as the check
        },
      },
    );
  }
  // Without a team there is nothing to check: 'ready' with no server
  const teamId = store.state.activeTeam?.id;
  trackSharedLoad('servers', () => (teamId
    ? axios.get('/cs_servers/running', { headers: { 'X-TEAM-ID': teamId } })
      .then(({ data }) => applyServers(data || [], false))
      .finally(() => subscribeToServers(teamId))
    : null));
  onUnmounted(() => {
    gone = true;
    serverSubscription?.unsubscribe();
    serverSubscription = null;
  });

  function terminateServer(id) {
    return axios.delete(`/cs_servers/${id}`, { headers: { 'X-TEAM-ID': store.state.activeTeam?.id } });
  }

  return {
    ...shell,
    user,
    siteSettings,
    serverLauncherDisabled,
    canStartServer,
    notices,
    unseenNotices,
    seeNotice,
    clearNotices,
    activeServer,
    activeServers,
    terminateServer,
  };
}
